import React, { useState } from "react";
import { useNavigate } from "react-router";
import { X } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useNotification } from "../../contexts/NotificationContext";
import { usePricing } from "../../contexts/PricingContext";
import { Place, flattenIssues, placeSchema } from "../../lib/pricing";
import { savePlace } from "../pricing/repository";
import { Field, FormSection, SelectField, SwitchField, TextField, inputClass } from "../components/pricing/fields";
import { PRICING_PATH } from "./ZoneForm";

interface Props {
  id?: string;
  place?: Place;
}

export default function PlaceForm({ id, place }: Props) {
  const { zones } = usePricing();
  const [name, setName] = useState(place?.name ?? "");
  const [zoneId, setZoneId] = useState(place?.zoneId ?? "");
  const [aliases, setAliases] = useState<string[]>(place?.aliases ?? []);
  const [aliasDraft, setAliasDraft] = useState("");
  const [active, setActive] = useState(place?.active ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const addAlias = () => {
    const value = aliasDraft.trim();
    if (!value) return;
    if (!aliases.some((a) => a.toLowerCase() === value.toLowerCase())) {
      setAliases([...aliases, value]);
    }
    setAliasDraft("");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = placeSchema.safeParse({ name, zoneId, aliases, active });
    if (!parsed.success) {
      setErrors(flattenIssues(parsed.error));
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await savePlace(parsed.data, id);
      showNotification("success", id ? "Local atualizado." : "Local criado.");
      navigate(`${PRICING_PATH}?tab=places`);
    } catch {
      showNotification("error", "Não foi possível salvar o local.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <FormSection title="Local">
        <TextField
          id="place-name"
          label="Nome *"
          placeholder="Ex.: Jurerê"
          value={name}
          maxLength={80}
          onChange={setName}
          error={errors.name}
        />
        <SelectField
          label="Região *"
          value={zoneId}
          onChange={setZoneId}
          options={zones.map((z) => ({ value: z.id, label: z.name }))}
          placeholder={zones.length ? "Selecione a região" : "Cadastre uma região primeiro"}
          error={errors.zoneId}
        />
        <Field
          label="Apelidos"
          htmlFor="place-alias"
          hint="Outras formas de escrever o local na busca (ex.: Jurere, Jurerê Internacional)."
        >
          <div className="flex gap-2">
            <Input
              id="place-alias"
              value={aliasDraft}
              maxLength={80}
              placeholder="Digite e pressione Enter"
              className={inputClass}
              onChange={(e) => setAliasDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addAlias();
                }
              }}
            />
            <Button type="button" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={addAlias}>
              Adicionar
            </Button>
          </div>
          {aliases.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {aliases.map((alias) => (
                <span
                  key={alias}
                  className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs text-white/90"
                >
                  {alias}
                  <button
                    type="button"
                    aria-label={`Remover ${alias}`}
                    className="rounded-full p-0.5 hover:bg-white/10"
                    onClick={() => setAliases(aliases.filter((a) => a !== alias))}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </Field>
        <SwitchField label="Local ativo" checked={active} onChange={setActive} />
      </FormSection>
      <Button type="submit" className="w-full" disabled={saving || zones.length === 0}>
        {id ? "Atualizar" : "Adicionar"}
      </Button>
    </form>
  );
}
